import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import IconFont from '$/components/IconFont';
import CommonExposure from '$/components/CommonExposure';
import { handleImageUrl } from '$/utils/image';
import { SkillCategory, SkillItem, SkillTrackParams } from './types';
import styles from './index.module.scss';

type SkillSelectPopupProps = {
  title?: React.ReactNode;
  categories: SkillCategory[];
  skillList: SkillItem[];
  selectedSkill?: SkillItem | null;
  onSelect?: (skill: SkillItem | null) => void;
  onShow?: (params: SkillTrackParams) => void;
  onSkillClick?: (params: SkillTrackParams) => void;
  onTabClick?: (params: SkillTrackParams) => void;
};

/** 渲染 Skill 选择弹框，标题文案由外层完成多语言适配后传入。 */
function SkillSelectPopup({
  title,
  categories,
  skillList,
  selectedSkill,
  onSelect,
  onShow,
  onSkillClick,
  onTabClick,
}: SkillSelectPopupProps) {
  const skillListRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] = useState<number>(
    categories[0]?.id || 0,
  );

  const categoryMap = useMemo(
    () => new Map(categories.map((category) => [category.id, category])),
    [categories],
  );

  const activeCategoryInfo = categoryMap.get(activeCategory) || categories[0];

  useEffect(() => {
    if (!categories.length || categoryMap.has(activeCategory)) return;
    setActiveCategory(categories[0].id);
  }, [activeCategory, categories, categoryMap]);

  const filteredSkills = useMemo(
    () => skillList.filter((s) => s.category_id === activeCategory),
    [skillList, activeCategory],
  );

  /** 点击 Skill 分类 tab 时同步更新选中态并触发业务埋点。 */
  const handleTabClick = useCallback((category: SkillCategory) => {
    onTabClick?.({ tab: category });
    setActiveCategory(category.id);
  }, [onTabClick]);

  /** 点击 Skill 内容时先上报点击，再交给外层处理选中/取消逻辑。 */
  const handleSkillClick = useCallback((skill: SkillItem, isSelected: boolean) => {
    const tab = categoryMap.get(skill.category_id) || activeCategoryInfo;
    if (tab) {
      onSkillClick?.({ tab, skill });
    }
    onSelect?.(isSelected ? null : skill);
  }, [activeCategoryInfo, categoryMap, onSelect, onSkillClick]);

  /** 限制技能列表滚轮只在列表内消费，避免边界滚动带动主页面。 */
  const handleSkillListWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    const skillListElement = skillListRef.current;
    if (!skillListElement) return;

    const {
      scrollTop,
      scrollHeight,
      clientHeight,
    } = skillListElement;
    const canScroll = scrollHeight > clientHeight;
    const isScrollingUp = event.deltaY < 0;
    const isScrollingDown = event.deltaY > 0;
    const atTop = scrollTop <= 0;
    const atBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;

    if (!canScroll || (isScrollingUp && atTop) || (isScrollingDown && atBottom)) {
      event.preventDefault();
    }

    event.stopPropagation();
  }, []);

  return (
    <div className={styles.skillPopup}>
      <div className={styles.skillPopupTitle}>{title}</div>

      <div className={styles.skillCategoryTabs}>
        {categories.map((cat) => (
          <div
            key={cat.id}
            className={classNames(styles.skillCategoryTab, {
              [styles.active]: activeCategory === cat.id,
            })}
            onClick={() => handleTabClick(cat)}
          >
            {cat.icon && <IconFont name={cat.icon} fontSize={14} className={styles.skillCategoryIcon} />}
            <span>{cat.name}</span>
          </div>
        ))}
      </div>

      <div
        ref={skillListRef}
        className={styles.skillList}
        onWheel={handleSkillListWheel}
      >
        {filteredSkills.map((skill) => {
          const isSelected = selectedSkill?.id === skill.id && selectedSkill?.category_id === skill.category_id;
          const tab = categoryMap.get(skill.category_id) || activeCategoryInfo;
          return (
            <CommonExposure
              key={skill.id}
              className={classNames(styles.skillItem, {
                [styles.selected]: isSelected,
              })}
              onlyOnce={false}
              onExposed={() => {
                if (!tab) return;
                onShow?.({ tab, skill });
              }}
              onClick={() => {
                handleSkillClick(skill, isSelected);
              }}
            >
              <div className={styles.skillItemIcon}>
                <img
                  src={skill.cover_list[0] ? handleImageUrl(skill.cover_list[0], { size: 40 }) : ''}
                  alt={skill.name}
                  className={styles.skillItemImage}
                  crossOrigin="anonymous"
                />
              </div>
              <div className={styles.skillItemContent}>
                <div className={styles.skillItemName}>{skill.name}</div>
                <div className={styles.skillItemDesc}>{skill.desc}</div>
              </div>
              {isSelected && (
                <IconFont name="checkBold" fontSize={12} className={styles.skillItemCheck} />
              )}
            </CommonExposure>
          );
        })}
      </div>
    </div>
  );
}

export default SkillSelectPopup;
